import $ from 'jquery'
import 'ckeditor'
import Vue from 'vue'
import { Store } from 'vuex'
import Component from 'vue-class-component'
import RootState from '../../store/state'
import { CONTENTS_CONTAINER_SELECTOR } from '../constants/ContainerSelector'
import { Watch } from 'vue-property-decorator'
import { Getters } from '../../store/editor/components/helpers'
import editor = CKEDITOR.editor

@Component({
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    baseId: {
      type: String,
      required: true
    },
    value: {
      type: String,
      required: false,
      default: ''
    },
    focus: {
      type: Boolean,
      required: true
    }
  }
})
export default class TextEditor extends Vue {
  $store: Store<RootState>
  readonly baseId: string
  readonly value: string
  readonly focus: boolean
  ckeditor: editor | false = false
  adjustToolbarOffsetHandler: undefined | (() => void)
  ready: boolean = false
  isPlaceholderVisible: boolean = false

  get toolbarElement(): HTMLElement {
    return this.getElementById(this.toolbarId)
  }

  get id(): string {
    return this.baseId + '__editor'
  }

  get toolbarId(): string {
    return this.id + '__toolbar'
  }

  get saveStateTimestamp(): number {
    return Getters.timeStampForSaveState(this.$store)
  }

  mounted(): void {
    this.$nextTick(() => {
      if (!document.getElementById(this.id)) {
        // 以下の手順でCKEditorのエラーが出てしまうので、エラーが出ないように回避する。
        //
        // * テキストを含むコンテンツを生成する
        // * コンテンツ管理画面に戻る（保存しなくて良い）
        // * コンテンツを新規作成し、コンテンツ編集画面に遷移する
        // * エラーが発生する
        //
        // 存在しないDOM要素に対してCKEditorを初期化しようとするのが原因。
        // 初期化前にDOM要素が存在するかをチェックすることで不具合を回避する。
        return
      }
      CKEDITOR.inline(this.id, {
        title: '',
        toolbar: [
          ['Format'],
          ['Bold', 'Italic', 'Underline', 'Strike'],
          ['BulletedList', 'NumberedList'],
          ['TextColor', 'BGColor'],
          ['JustifyLeft', 'JustifyCenter', 'JustifyRight'],
          ['Link', 'Unlink']
        ],
        sharedSpaces: {
          // ここで指定した要素にツールバーを生成する
          top: this.toolbarId
        },
        format_tags: 'p;h3',
        on: {
          instanceReady: () => {
            const editor = CKEDITOR.instances[this.id]
            Vue.set(this, 'ckeditor', editor)
            this.setContent(this.value)
            this.ready = true
            this.refreshPlaceholder()
          },
          focus: () => {
            this.isPlaceholderVisible = false
          },
          blur: () => {
            this.onContentChanged()
            this.refreshPlaceholder()
          }
        }
      })

      // リンクの編集時、 target="_blank" をデフォルト値にする
      const editor: any = CKEDITOR
      editor.on('dialogDefinition', (event: any) => {
        const name: string = event.data.name
        if (name === 'link') {
          const definition: any = event.data.definition
          const targetTab = definition.getContents('target')
          const targetTypeField = targetTab.get('linkTargetType')
          targetTypeField.default = '_blank'
        }
      })
    })
  }

  beforeDestroy(): void {
    if (this.ckeditor) {
      this.ckeditor.destroy()
    }
  }

  onContentChanged(): void {
    this.adjustToolbarOffset()
    // 初期値を設定するときは、 change イベントを発火しない。
    // テキストを変更していないのに「記事の変更を保存せず終了しますか？」の確認ダイアログが出るのを防ぐ。
    if (this.ready) {
      this.emitChange()
    }
  }

  getContent(): string {
    // console.log('getContent', this.ckeditor.getData())
    // キャッシュが有効な computed ではなく method にする。
    // ckeditor 経由で取得する値がキャッシュされると、不整合を起こすため。
    return this.ckeditor ? this.ckeditor.getData() : ''
  }

  isContentEmpty(): boolean {
    return !this.getContent()
  }

  setContent(content: string): void {
    if (this.ckeditor && this.ckeditor.getData() !== content) {
      this.ckeditor.setData(content)
    }
  }

  emitChange(): void {
    this.$emit('change', this.getContent())
  }

  @Watch('value')
  updateContent(): void {
    this.setContent(this.value)
    this.refreshPlaceholder()
  }

  refreshPlaceholder(): void {
    this.isPlaceholderVisible = this.isContentEmpty()
  }

  getElementById(id: string): HTMLElement {
    const element = document.getElementById(id)
    if (!element) {
      throw new Error(`要素が見つかりません: id=${id}`)
    }
    return element
  }

  @Watch('focus')
  onFocusChanged(): void {
    if (this.focus) {
      this.$nextTick(() => {
        if (this.adjustToolbarOffsetHandler || !this.ckeditor) {
          return
        }
        this.adjustToolbarOffset()
        this.adjustToolbarOffsetHandler = () => {
          this.adjustToolbarOffset()
        }
        $(CONTENTS_CONTAINER_SELECTOR).on('scroll', this.adjustToolbarOffsetHandler)
      })
    } else {
      $(CONTENTS_CONTAINER_SELECTOR).off('scroll', this.adjustToolbarOffsetHandler)
      this.adjustToolbarOffsetHandler = undefined
    }
  }

  adjustToolbarOffset(): void {
    this.toolbarElement.style.cssText = ''
    this.toolbarElement.style.cssText = (this.calcToolbarHorizontalAlign() + this.calcToolbarVerticalAlign())
  }

  calcToolbarHorizontalAlign(): string {
    const toolbarRight = this.toolbarElement.getBoundingClientRect().right
    const containerRight = $(CONTENTS_CONTAINER_SELECTOR).get(0).getBoundingClientRect().right
    return ((toolbarRight - containerRight) > 0) ? 'left: auto; right: 0;' : ''
  }

  calcToolbarVerticalAlign(): string {
    const container = $(CONTENTS_CONTAINER_SELECTOR).get(0)
    const containerRect = container.getBoundingClientRect()
    const toolbarRect = this.toolbarElement.getBoundingClientRect()
    const offsetTop = toolbarRect.top - containerRect.top
    if (offsetTop >= 0) {
      return ''
    }

    // ツールバーの位置が、画面上部にはみ出ているので、画面内に移動する
    const elementRect = this.$el.getBoundingClientRect()
    const offsetBottom = containerRect.bottom - elementRect.bottom - toolbarRect.height
    if (offsetBottom >= 0) {
      // コンテナの下側が画面内にある場合、コンテナの下側にくっつける
      return `top: 100%; bottom: auto;`
    }
    // コンテナの下側が見えていない場合、画面上に表示する
    return `margin-bottom: ${offsetTop}px;`
  }

  @Watch('saveStateTimestamp')
  saveState(): void {
    if (this.ckeditor) {
      // blur イベントを強制的に発火して、テキストの変更を確定する。
      // CKEditor は blur イベントの発火までタイムラグがあり、テキストの変更が保存データに反映されない恐れがあるため。
      this.ckeditor.focusManager.blur(true)
    }
  }
}
